import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, fromJS } from 'immutable';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import ModalActions from 'flux/actions/ModalActions';
import UIRegions from 'shared/constants/UIRegions';
import PublishGlobalElementsActions from 'flux/actions/PublishGlobalElementsActions';
import AlertActions from 'flux/actions/AlertActions';
import AlertTypes from 'shared/constants/AlertTypes';
import { isGlobalElementManager } from 'main/components/show/cuesheet/cuesheetViewHelper';

const geManagerShowId = 'geManagerShowId';

/**
 *  Global Element Store
 */
export default Reflux.createStore({
  listenables: [PublishGlobalElementsActions],

  mixins: [BaseMixin],

  init() {
    this.setStateAndTrigger(
      Map({
        globalElementShowItems: OrderedMap(),
        selectedItems: OrderedMap(),
        allItemsSelected: false,
        searchedItems: null,
        searchTerm: '',
        submitText: 'Publish Changes',
        submitDisabled: false,
      }),
    );

    this.listenTo(CuesheetStore, 'onCuesheetChange');
  },

  onCuesheetChange(state) {
    if (!state.get('rowMap')) return;

    let next = this.getState()
      .set('teamId', state.get('teamId'))
      .set('eventId', state.get('eventId'))
      .set('showId', state.get('showId'))
      .set('itemSetListId', state.get('itemSetListId'))
      .set('itemSetId', state.get('rowMap').keySeq().first());

    if (state.get('selectedCells').size > 0) {
      const { rowId } = state.get('selectedCells').last();
      next = next.set('rowId', rowId);
    }

    this.setStateAndTrigger(next);
  },

  onPopulate() {
    const state = this.getState();

    PublishGlobalElementsActions.getGlobalElementShowItems(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetId'),
      state.get('rowId'),
    );
  },

  onPopulateForShowRow(globalElement) {
    let state = this.getState();

    state = state.set('rowId', globalElement.get('row'));

    PublishGlobalElementsActions.getGlobalElementShowItemsForShowRow(
      state.get('teamId'),
      globalElement.get('sourceEvent'),
      globalElement.get('sourceShow'),
      globalElement.get('sourceItemSet'),
      globalElement.get('sourceRow'),
    );

    this.setStateAndTrigger(state);
  },

  onGetGlobalElementShowItemsForShowRowCompleted(globalElementShowItems) {
    let next = this.getState();

    next = this._populateGlobalElementShowItems(globalElementShowItems, next);
    next = this._toggleAllItemSelection(next);

    this.setStateAndTrigger(next);
  },

  onGetGlobalElementShowItemsCompleted(globalElementShowItems) {
    let next = this.getState();

    next = this._populateGlobalElementShowItems(globalElementShowItems, next);
    next = this._toggleAllItemSelection(next);

    this.setStateAndTrigger(next);
  },

  _populateGlobalElementShowItems(globalElementShowItems, state) {
    let next = state || this.getState();

    let geLookup = {};

    globalElementShowItems.sort((a, b) => {
      if (a.show.startOn < b.show.startOn) return -1;
      if (a.show.startOn > b.show.startOn) return 1;
      return 0;
    });

    const items = OrderedMap().withMutations((next) => {
      if (!isGlobalElementManager()) {
        next.set(
          geManagerShowId,
          fromJS({
            _id: geManagerShowId,
          }),
        );
      }

      globalElementShowItems.forEach((ge) => {
        //Prevent duplicate shows from showing in publish modal list
        let showId = ge.show._id;
        if (geLookup[showId]) return;
        geLookup[showId] = true;

        next.set(ge._id, fromJS(ge));
      });

      return next;
    });

    next = next.set('globalElementShowItems', items);

    if (state) return next;

    this.setStateAndTrigger(next);
  },

  onToggleItemSelection(itemId) {
    this._toggleItemSelection(itemId);
  },

  _toggleItemSelection(itemId, state) {
    let next = state || this.getState();

    let totalNumShowItems = next.get('globalElementShowItems').size;

    // Note - this seems like a bug, it is not clear why this is here
    // eslint-disable-next-line no-unused-expressions
    if (!isGlobalElementManager()) totalNumShowItems;

    next = next.update('selectedItems', (selectedItems) => {
      if (selectedItems.get(itemId)) return selectedItems.delete(itemId);
      return selectedItems.set(itemId, true);
    });

    const numSelectedItems = next.get('selectedItems').size;

    if (numSelectedItems < totalNumShowItems)
      next = next.set('allItemsSelected', false);

    if (numSelectedItems === totalNumShowItems)
      next = next.set('allItemsSelected', true);

    if (state) {
      return next;
    }

    this.setStateAndTrigger(next);
  },

  onToggleAllItemSelection() {
    this._toggleAllItemSelection();
  },

  _toggleAllItemSelection(state) {
    let next = state || this.getState();

    const globalElementShowItems = next.get('globalElementShowItems');

    // Clear search when using control
    PublishGlobalElementsActions.clearSearch();

    /* Don't know what this is used for. Leave uncommented until we figure it
     * out
     *
    if (selectedItems.get(globalElementShowItems.first().get('_id'))) {
      next = next
        .set('selectedItems', OrderedMap())
        .set('allItemsSelected', false);

      return this.setStateAndTrigger(next);
    }*/

    const totalNumShowItems = next.get('globalElementShowItems').size;
    const numSelectedItems = next.get('selectedItems').size;

    if (numSelectedItems < totalNumShowItems) {
      next = next.set('allItemsSelected', true);
      next = next.set('selectedItems', globalElementShowItems);
    }

    //If the number of selected items matches the total number of items
    //then all items are currently toggled on. We toggle them off by setting
    //'allItemsSelected' to false and 'selectedItems' to an empty map
    if (numSelectedItems === totalNumShowItems) {
      next = next.set('allItemsSelected', false);
      next = next.set('selectedItems', OrderedMap());
    }

    if (state) {
      return next;
    }

    return this.setStateAndTrigger(next);
  },

  onSearchItems(searchTerm) {
    let next = this.getState();
    next = next
      .set('searchTerm', searchTerm)
      .set('selectedItems', OrderedMap())
      .set('allItemsSelected', false);

    const globalElementShowItems = next.get('globalElementShowItems').toArray();
    const searchRegex = new RegExp(searchTerm, 'ig');

    let itemMatches = globalElementShowItems.filter((i) => {
      return i.getIn(['show', 'title']).match(searchRegex);
    });

    if (itemMatches.length > 0) next = next.set('searchedItems', itemMatches);

    this.setStateAndTrigger(next);
  },

  onClearSelection() {
    this.setInStateAndTrigger('selectedItems', OrderedMap());
  },

  onClearSearch() {
    let next = this.getState();

    next = next.set('searchTerm', '').set('searchedItems', null);

    this.setStateAndTrigger(next);
  },

  onPublishChangesEverywhere() {
    let next = this.getState();

    next = next.set('submitDisabled', true).set('submitText', 'Working...');

    this.setStateAndTrigger(next);
  },

  _clearSubmitState() {
    let next = this.getState();

    next = next
      .set('submitDisabled', false)
      .set('submitText', 'Publish Changes')
      .set('selectedItems', OrderedMap());

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onPublishChangesCompleted(result) {
    this._clearSubmitState();
    ModalActions.hide(UIRegions.PublishGEChanges);
    AlertActions.show(
      'Your changes have been successfully published',
      UIRegions.Cuesheet,
      AlertTypes.successAlert,
    );
  },

  // eslint-disable-next-line no-unused-vars
  onPublishChangesEverywhereCompleted(result) {
    this._clearSubmitState();
    ModalActions.hide(UIRegions.PublishGEChanges);
    AlertActions.show(
      'Your changes have been successfully published',
      UIRegions.Cuesheet,
      AlertTypes.successAlert,
    );
  },
});
